import React, { useState, useRef } from 'react';

export default function ASTGridEditor({
  isolate,
  antimicrobials = [],
  onValueChange,
  onAddDrug,
  onOpenOverride,
  onToggleReportable,
  onBulkUpdate
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [methodFilter, setMethodFilter] = useState('ALL'); // 'ALL' | 'Disk' | 'MIC'
  const [selectedNewDrugId, setSelectedNewDrugId] = useState('');
  const inputRefs = useRef({});

  if (!isolate) return null;

  const astList = isolate.astResults || [];

  // Filtered AST list
  const filteredList = astList.filter((row) => {
    const matchesSearch =
      !searchTerm ||
      (row.drugName && row.drugName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (row.drugCode && row.drugCode.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (row.className && row.className.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesMethod =
      methodFilter === 'ALL' ||
      (methodFilter === 'Disk' && (row.methodName?.includes('Disk') || row.measuredUnit === 'mm')) ||
      (methodFilter === 'MIC' && (row.methodName?.includes('MIC') || row.measuredUnit?.includes('µg')));

    return matchesSearch && matchesMethod;
  });

  // Keyboard navigation: Enter or Tab to next input
  const handleKeyDown = (e, index) => {
    if (e.key === 'Enter' || e.key === 'ArrowDown') {
      e.preventDefault();
      const nextInput = inputRefs.current[index + 1];
      if (nextInput) {
        nextInput.focus();
        nextInput.select();
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prevInput = inputRefs.current[index - 1];
      if (prevInput) {
        prevInput.focus();
        prevInput.select();
      }
    }
  };

  const handleAddSelectedDrug = (e) => {
    const drugId = e.target.value;
    if (drugId) {
      onAddDrug(drugId);
      setSelectedNewDrugId('');
    }
  };

  // Bulk actions
  const handleSetAllReportable = (reportable) => {
    if (onBulkUpdate) {
      const updated = astList.map((r) => ({ ...r, isReportable: reportable }));
      onBulkUpdate(updated);
    }
  };

  return (
    <div className="wb-ast-editor-card">
      {/* Standard Banner & AST Toolbar */}
      <div className="wb-ast-standard-banner">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '1.2rem' }}>⚖️</span>
          <div>
            <strong style={{ fontSize: '0.86rem', color: '#0f172a' }}>
              Active Clinical Standard: EUCAST Clinical Breakpoint Guidelines
            </strong>
            <span style={{ fontSize: '0.74rem', color: '#64748b', marginLeft: '8px' }}>
              Version 16.1 &bull; 2026 Edition &bull; Breakpoints automatically applied
            </span>
          </div>
        </div>
      </div>

      <div className="wb-ast-toolbar">
        <div className="wb-ast-toolbar-left">
          {/* Search Box */}
          <div className="wb-ast-search-box">
            <span style={{ color: '#94a3b8' }}>🔍</span>
            <input
              type="text"
              placeholder="Search antibiotic name, code or class..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="wb-ast-search-input"
              style={{ width: '220px' }}
            />
            {searchTerm && (
              <button
                type="button"
                className="wb-clear-btn"
                onClick={() => setSearchTerm('')}
              >
                &times;
              </button>
            )}
          </div>

          {/* Method Filter */}
          <div className="wb-method-filter-group">
            <button
              type="button"
              className={`wb-filter-btn ${methodFilter === 'ALL' ? 'active' : ''}`}
              onClick={() => setMethodFilter('ALL')}
            >
              All ({astList.length})
            </button>
            <button
              type="button"
              className={`wb-filter-btn ${methodFilter === 'Disk' ? 'active' : ''}`}
              onClick={() => setMethodFilter('Disk')}
            >
              Disk Diffusion (mm)
            </button>
            <button
              type="button"
              className={`wb-filter-btn ${methodFilter === 'MIC' ? 'active' : ''}`}
              onClick={() => setMethodFilter('MIC')}
            >
              MIC (µg/mL)
            </button>
          </div>
        </div>

        <div className="wb-ast-toolbar-right">
          {/* Add Antimicrobial Dropdown */}
          <select
            className="wb-ast-add-select"
            value={selectedNewDrugId}
            onChange={handleAddSelectedDrug}
          >
            <option value="">+ Add Antimicrobial to Grid...</option>
            {antimicrobials
              .filter((d) => !astList.some((r) => r.antimicrobialID === d.antimicrobialID))
              .map((d) => (
                <option key={d.antimicrobialID} value={d.antimicrobialID}>
                  {d.drugName} ({d.drugCode || 'Rx'}) &bull; {d.className || 'Antimicrobial'}
                </option>
              ))}
          </select>

          {/* Bulk actions */}
          <button
            type="button"
            className="wb-tool-btn"
            onClick={() => handleSetAllReportable(true)}
            title="Mark all antimicrobials as reportable in official printout"
          >
            ✓ All Reportable
          </button>
        </div>
      </div>

      {/* Keyboard Shortcuts Hint Bar */}
      <div className="wb-keyboard-hints-bar">
        <span>⚡ <strong>Rapid Data Entry:</strong> Type numeric value &rarr; Press <kbd>Tab</kbd> / <kbd>Enter</kbd> / <kbd>&darr;</kbd> to auto-advance. Real-time EUCAST/CLSI S/I/R interpretation calculates instantly.</span>
      </div>

      {/* AST Susceptibility Table */}
      <div className="micro-table-wrapper">
        <table className="ast-grid-table">
          <thead>
            <tr>
              <th style={{ width: '36px' }}>#</th>
              <th>Antibiotic</th>
              <th>Class</th>
              <th style={{ width: '110px' }}>Method</th>
              <th style={{ width: '110px' }}>Zone (mm)</th>
              <th style={{ width: '110px' }}>MIC (µg/mL)</th>
              <th style={{ width: '140px' }}>Interpretation</th>
              <th>Clinical Breakpoint & Rule Trace</th>
              <th style={{ width: '80px', textAlign: 'center' }}>Report</th>
              <th style={{ width: '80px', textAlign: 'center' }}>Override</th>
            </tr>
          </thead>
          <tbody>
            {filteredList.length === 0 ? (
              <tr>
                <td colSpan="10" style={{ textAlign: 'center', padding: '36px', color: '#94a3b8' }}>
                  {searchTerm
                    ? `No antimicrobials found matching "${searchTerm}".`
                    : 'No antimicrobials configured for this isolate. Use "+ Add Antimicrobial" above.'}
                </td>
              </tr>
            ) : (
              filteredList.map((row, idx) => {
                const isDisk = row.methodName?.includes('Disk') || row.measuredUnit === 'mm';
                const interp = row.finalInterpretation || row.calculatedInterpretation || 'NB';

                let badgeClass = 'badge-nb';
                if (interp === 'S') badgeClass = 'badge-s';
                else if (interp === 'R') badgeClass = 'badge-r';
                else if (interp === 'I') badgeClass = 'badge-i';
                else if (interp === 'SDD') badgeClass = 'badge-i';

                return (
                  <tr key={row.antimicrobialID} className={row.isOverridden ? 'row-overridden' : ''}>
                    <td style={{ color: '#94a3b8', fontSize: '0.76rem' }}>{idx + 1}</td>
                    <td>
                      <div className="wb-drug-name-cell">
                        <strong>{row.drugName}</strong>
                        <span style={{ fontSize: '0.72rem', color: '#64748b' }}>
                          Code: <code>{row.drugCode || '—'}</code>
                        </span>
                        {row.isOverridden && (
                          <span className="wb-override-pill" title={`Clinical override: ${row.overrideReason}`}>
                            Manual Override
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="wb-method-tag">
                        {row.className || 'Standard Class'}
                      </span>
                    </td>
                    <td>
                      <span className="wb-method-tag">
                        {row.methodName || (isDisk ? 'Disk Diffusion' : 'MIC')}
                      </span>
                    </td>
                    <td>
                      {isDisk ? (
                        <div className="wb-ast-input-group">
                          <input
                            type="number"
                            step="1"
                            min="0"
                            max="60"
                            ref={(el) => (inputRefs.current[idx] = el)}
                            defaultValue={row.measuredValue !== null && row.measuredValue !== undefined ? row.measuredValue : ''}
                            placeholder="mm"
                            className="ast-input-val"
                            onBlur={(e) => onValueChange(row.antimicrobialID, e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, idx)}
                          />
                          <span className="wb-ast-unit-label">mm</span>
                        </div>
                      ) : (
                        <span style={{ color: '#94a3b8', fontSize: '0.78rem' }}>—</span>
                      )}
                    </td>
                    <td>
                      {!isDisk ? (
                        <div className="wb-ast-input-group">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            max="1024"
                            ref={(el) => (inputRefs.current[idx] = el)}
                            defaultValue={row.measuredValue !== null && row.measuredValue !== undefined ? row.measuredValue : ''}
                            placeholder="µg/mL"
                            className="ast-input-val"
                            onBlur={(e) => onValueChange(row.antimicrobialID, e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, idx)}
                          />
                          <span className="wb-ast-unit-label">µg/mL</span>
                        </div>
                      ) : (
                        <span style={{ color: '#94a3b8', fontSize: '0.78rem' }}>—</span>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span className={`micro-badge ${badgeClass}`} style={{ fontSize: '0.88rem', fontWeight: '800' }}>
                          {interp}
                        </span>
                        <span style={{ fontSize: '0.74rem', color: '#64748b' }}>
                          {interp === 'S'
                            ? 'Susceptible'
                            : interp === 'R'
                            ? 'Resistant'
                            : interp === 'I'
                            ? 'Intermediate'
                            : interp === 'SDD'
                            ? 'Dose Dependent'
                            : 'No Breakpoint'}
                        </span>
                      </div>
                    </td>
                    <td style={{ fontSize: '0.78rem', color: '#475569', maxWidth: '300px' }}>
                      {row.ruleTrace ? (
                        <span className="wb-rule-trace-text">{row.ruleTrace}</span>
                      ) : row.measuredValue ? (
                        <span style={{ color: '#059669' }}>
                          EUCAST Breakpoint standard applied ({row.appliedStandard || 'EUCAST v16.1'})
                        </span>
                      ) : (
                        <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>
                          Awaiting measurement entry
                        </span>
                      )}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={row.isReportable !== false}
                        onChange={(e) => onToggleReportable(row.antimicrobialID, e.target.checked)}
                        style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                        title="Include in patient official report"
                      />
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <button
                        type="button"
                        className="wb-row-override-btn"
                        onClick={() => onOpenOverride(row)}
                        title="Clinically override automated interpretation"
                      >
                        Override
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
